/**
 * Guest-friendly wishlist (no login required).
 * - Guests: localStorage + cookie mirror
 * - Logged-in: merge guest list into customer-scoped storage on first load
 */
(function () {
  const GUEST_KEY = 'wp_wishlist';
  const CUSTOMER_PREFIX = 'wp_wishlist_customer_';
  const COOKIE_KEY = 'wp_wishlist';
  const EVENT = 'wishlist:updated';
  const MAX_ITEMS = 100;

  function customerId() {
    return (window.ShopifyWishlist && window.ShopifyWishlist.customerId) || null;
  }

  function storageKey() {
    const id = customerId();
    return id ? CUSTOMER_PREFIX + id : GUEST_KEY;
  }

  function readRaw(key) {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return [];
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch (e) {
      return [];
    }
  }

  function writeRaw(key, items) {
    try {
      localStorage.setItem(key, JSON.stringify(items.slice(0, MAX_ITEMS)));
    } catch (e) {
      /* quota / private mode */
    }
  }

  function setCookie(ids) {
    try {
      const value = encodeURIComponent(ids.join(','));
      const maxAge = 60 * 60 * 24 * 365;
      document.cookie = `${COOKIE_KEY}=${value}; path=/; max-age=${maxAge}; SameSite=Lax`;
    } catch (e) {
      /* ignore */
    }
  }

  function normalizeItem(item) {
    if (!item || item.id == null) return null;
    return {
      id: String(item.id),
      handle: item.handle || '',
      title: item.title || '',
      url: item.url || (item.handle ? `/products/${item.handle}` : ''),
      image: item.image || '',
      price: item.price || '',
      vendor: item.vendor || '',
      addedAt: item.addedAt || Date.now(),
    };
  }

  function dedupe(items) {
    const seen = new Set();
    const out = [];
    items.forEach((raw) => {
      const item = normalizeItem(raw);
      if (!item || seen.has(item.id)) return;
      seen.add(item.id);
      out.push(item);
    });
    return out;
  }

  function mergeLists(a, b) {
    return dedupe([].concat(a || [], b || []));
  }

  /**
   * When a customer logs in, fold the guest wishlist into their account-scoped list
   * so saved products carry over without requiring them to re-save.
   */
  function syncGuestToCustomer() {
    const id = customerId();
    if (!id) return;
    const guest = readRaw(GUEST_KEY);
    const account = readRaw(CUSTOMER_PREFIX + id);
    if (!guest.length && !account.length) return;
    const merged = mergeLists(account, guest);
    writeRaw(CUSTOMER_PREFIX + id, merged);
    if (guest.length) {
      writeRaw(GUEST_KEY, []);
    }
    setCookie(merged.map((i) => i.id));
  }

  function getItems() {
    syncGuestToCustomer();
    return dedupe(readRaw(storageKey()));
  }

  function saveItems(items) {
    const list = dedupe(items);
    writeRaw(storageKey(), list);
    /* Keep guest key aligned when logged out; when logged in account key is primary */
    if (!customerId()) {
      writeRaw(GUEST_KEY, list);
    }
    setCookie(list.map((i) => i.id));
    document.dispatchEvent(
      new CustomEvent(EVENT, {
        detail: { items: list, count: list.length },
      })
    );
    return list;
  }

  function has(productId) {
    const id = String(productId);
    return getItems().some((i) => i.id === id);
  }

  function add(item) {
    const normalized = normalizeItem(item);
    if (!normalized) return getItems();
    const items = getItems();
    if (items.some((i) => i.id === normalized.id)) return items;
    items.unshift(normalized);
    return saveItems(items);
  }

  function remove(productId) {
    const id = String(productId);
    return saveItems(getItems().filter((i) => i.id !== id));
  }

  function toggle(item) {
    if (!item || item.id == null) return { items: getItems(), added: false };
    const id = String(item.id);
    if (has(id)) {
      return { items: remove(id), added: false };
    }
    return { items: add(item), added: true };
  }

  function updateButtons(root) {
    const scope = root || document;
    const items = getItems();
    const ids = new Set(items.map((i) => i.id));
    scope.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      const id = String(btn.getAttribute('data-product-id') || '');
      const active = ids.has(id);
      btn.classList.toggle('is-active', active);
      btn.setAttribute('aria-pressed', active ? 'true' : 'false');
      const labelAdd = btn.getAttribute('data-label-add') || 'Save to wishlist';
      const labelRemove = btn.getAttribute('data-label-remove') || 'Remove from wishlist';
      btn.setAttribute('aria-label', active ? labelRemove : labelAdd);
      const outline = btn.querySelector('[data-wishlist-icon="outline"]');
      const filled = btn.querySelector('[data-wishlist-icon="filled"]');
      if (outline) outline.hidden = active;
      if (filled) filled.hidden = !active;
    });

    document.querySelectorAll('[data-wishlist-count]').forEach((el) => {
      const count = items.length;
      el.textContent = count > 0 ? String(count) : '';
      el.hidden = count === 0;
      el.classList.toggle('is-empty', count === 0);
    });

    document.querySelectorAll('[data-wishlist-count-bubble]').forEach((el) => {
      el.hidden = items.length === 0;
    });
  }

  function bindToggles(root) {
    const scope = root || document;
    scope.querySelectorAll('[data-wishlist-toggle]').forEach((btn) => {
      if (btn.dataset.wishlistBound === '1') return;
      btn.dataset.wishlistBound = '1';
      btn.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        const item = {
          id: btn.getAttribute('data-product-id'),
          handle: btn.getAttribute('data-product-handle') || '',
          title: btn.getAttribute('data-product-title') || '',
          url: btn.getAttribute('data-product-url') || '',
          image: btn.getAttribute('data-product-image') || '',
          price: btn.getAttribute('data-product-price') || '',
          vendor: btn.getAttribute('data-product-vendor') || '',
        };
        const result = toggle(item);
        updateButtons();
        btn.classList.add('wishlist-btn--pulse');
        window.setTimeout(() => btn.classList.remove('wishlist-btn--pulse'), 280);
        if (window.ShopifyWishlist && typeof window.ShopifyWishlist.onToggle === 'function') {
          window.ShopifyWishlist.onToggle(result);
        }
      });
    });
  }

  function formatMoney(cents, currency) {
    try {
      if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && window.theme && window.theme.moneyFormat) {
        return window.Shopify.formatMoney(cents, window.theme.moneyFormat);
      }
    } catch (e) {
      /* fall through */
    }
    const amount = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: currency || (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || 'INR',
      }).format(amount);
    } catch (e) {
      return 'Rs. ' + amount.toFixed(2);
    }
  }

  function buildCardHtml(product, saved) {
    const image =
      (product.featured_image && (product.featured_image.src || product.featured_image)) ||
      (product.images && product.images[0]) ||
      saved.image ||
      '';
    const title = product.title || saved.title || 'Product';
    const url = product.url || saved.url || (saved.handle ? '/products/' + saved.handle : '#');
    const priceCents =
      product.price != null
        ? product.price
        : product.price_min != null
          ? product.price_min
          : null;
    const priceLabel =
      priceCents != null ? formatMoney(priceCents, product.currency || undefined) : saved.price || '';
    const id = product.id || saved.id;
    const handle = product.handle || saved.handle || '';
    const vendor = product.vendor || saved.vendor || '';
    const imgTag = image
      ? `<img src="${image}" alt="${escapeHtml(title)}" width="533" height="533" loading="lazy" class="motion-reduce">`
      : '';

    return `
      <li class="grid__item" data-wishlist-item="${escapeHtml(String(id))}">
        <div class="card-wrapper product-card-wrapper underline-links-hover">
          <div class="card card--${(window.ShopifyWishlist && window.ShopifyWishlist.cardStyle) || 'standard'} card--media">
            <div class="card__inner ratio" style="--ratio-percent: 100%;">
              <div class="card__media">
                <div class="media media--transparent">
                  ${imgTag}
                </div>
                <button
                  type="button"
                  class="wishlist-btn is-active"
                  data-wishlist-toggle
                  data-product-id="${escapeHtml(String(id))}"
                  data-product-handle="${escapeHtml(handle)}"
                  data-product-title="${escapeHtml(title)}"
                  data-product-url="${escapeHtml(url)}"
                  data-product-image="${escapeHtml(image)}"
                  data-product-price="${escapeHtml(priceLabel)}"
                  data-product-vendor="${escapeHtml(vendor)}"
                  data-label-add="Save to wishlist"
                  data-label-remove="Remove from wishlist"
                  aria-pressed="true"
                  aria-label="Remove from wishlist"
                >
                  <span data-wishlist-icon="outline" hidden>${heartOutlineSvg()}</span>
                  <span data-wishlist-icon="filled">${heartFilledSvg()}</span>
                </button>
              </div>
            </div>
            <div class="card__content">
              <div class="card__information">
                <h3 class="card__heading h5">
                  <a href="${escapeHtml(url)}" class="full-unstyled-link">${escapeHtml(title)}</a>
                </h3>
                <div class="card-information">
                  <div class="price">
                    <div class="price__container">
                      <div class="price__regular">
                        <span class="price-item price-item--regular">${escapeHtml(priceLabel)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </li>`;
  }

  function heartOutlineSvg() {
    return `<svg class="icon icon-heart" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" fill="none"/></svg>`;
  }

  function heartFilledSvg() {
    return `<svg class="icon icon-heart icon-heart--filled" aria-hidden="true" focusable="false" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" fill="currentColor" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>`;
  }

  function escapeHtml(str) {
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  async function fetchProduct(handle) {
    if (!handle) return null;
    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    const url = `${root}products/${encodeURIComponent(handle)}.js`;
    try {
      const res = await fetch(url);
      if (!res.ok) return null;
      return await res.json();
    } catch (e) {
      return null;
    }
  }

  async function renderWishlistPage() {
    const root = document.querySelector('[data-wishlist-page]');
    if (!root) return;

    const grid = root.querySelector('[data-wishlist-grid]');
    const empty = root.querySelector('[data-wishlist-empty]');
    const loading = root.querySelector('[data-wishlist-loading]');
    const items = getItems();

    if (loading) loading.hidden = false;
    if (empty) empty.hidden = true;
    if (grid) grid.innerHTML = '';

    if (!items.length) {
      if (loading) loading.hidden = true;
      if (empty) empty.hidden = false;
      if (grid) grid.hidden = true;
      return;
    }

    if (grid) grid.hidden = false;

    const cards = await Promise.all(
      items.map(async (saved) => {
        const product = saved.handle ? await fetchProduct(saved.handle) : null;
        if (!product && !saved.url) return null;
        return buildCardHtml(product || {}, saved);
      })
    );

    if (grid) {
      grid.innerHTML = cards.filter(Boolean).join('');
      bindToggles(grid);
      updateButtons(grid);
    }
    if (loading) loading.hidden = true;
    if (!cards.filter(Boolean).length && empty) {
      empty.hidden = false;
      if (grid) grid.hidden = true;
    }
  }

  function init() {
    syncGuestToCustomer();
    bindToggles(document);
    updateButtons(document);
    renderWishlistPage();
  }

  document.addEventListener(EVENT, () => {
    updateButtons(document);
    /* Re-render wishlist page when items change (e.g. remove heart) */
    if (document.querySelector('[data-wishlist-page]')) {
      renderWishlistPage();
    }
  });

  /* Section re-renders / theme editor */
  document.addEventListener('shopify:section:load', (e) => {
    bindToggles(e.target);
    updateButtons(e.target);
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.ShopifyWishlist = Object.assign(window.ShopifyWishlist || {}, {
    getItems,
    has,
    add,
    remove,
    toggle,
    updateButtons,
    bindToggles,
    renderWishlistPage,
  });
})();
